import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Badge, Modal, Button } from "react-bootstrap";
import { FiPrinter, FiSend, FiEdit2 } from "react-icons/fi";
import { MdLocalLaundryService } from "react-icons/md";
import {
  TickCircle,
  Magicpen,
  Refresh2,
  Box,
  Tag2,
  CloseCircle,
} from "iconsax-react";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
import bluetoothPrinter from "../utils/bluetooth";

const TransactionDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [tx, setTx] = useState(null);
  const [loading, setLoading] = useState(true);

  // Mesin cuci
  const [washingMachines, setWashingMachines] = useState([]);
  const [showMachineModal, setShowMachineModal] = useState(false);
  const [selectedMachineIds, setSelectedMachineIds] = useState([]);
  const [machineSaving, setMachineSaving] = useState(false);

  useEffect(() => {
    loadTransaction();
  }, [id]);

  useEffect(() => {
    api
      .get("/washing-machines/active")
      .then((res) => setWashingMachines(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  const loadTransaction = async () => {
    try {
      const res = await api.get(`/transactions/${id}`);
      setTx(res.data);
    } catch (error) {
      toast.error("Gagal memuat transaksi");
    } finally {
      setLoading(false);
    }
  };

  // Transaksi punya proses cuci? (kategori kosong = anggap ada, sama seperti di list)
  const txCats = (tx?.service_categories || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const txHasWash =
    txCats.length === 0 ||
    txCats.some((c) => c === "Cuci Kering" || c === "Cuci + Setrika");
  const canEditMachines =
    txHasWash &&
    tx?.status !== "cancelled" &&
    tx?.status !== "diterima" &&
    user?.role !== "penyetrika";

  const openMachineModal = () => {
    setSelectedMachineIds((tx?.machines || []).map((m) => m.id));
    setShowMachineModal(true);
  };

  const toggleMachine = (machineId) => {
    setSelectedMachineIds((prev) =>
      prev.includes(machineId)
        ? prev.filter((m) => m !== machineId)
        : [...prev, machineId],
    );
  };

  const saveMachines = async () => {
    if (selectedMachineIds.length === 0) {
      toast.error("Pilih minimal satu mesin cuci");
      return;
    }
    setMachineSaving(true);
    try {
      const res = await api.put(`/transactions/${id}/machines`, {
        machine_ids: selectedMachineIds,
      });
      setTx((prev) => ({ ...prev, machines: res.data.machines || [] }));
      toast.success("Mesin cuci diperbarui");
      setShowMachineModal(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal menyimpan mesin cuci");
    } finally {
      setMachineSaving(false);
    }
  };

  // WhatsApp Web integration
  const openWAWeb = async () => {
    try {
      // Gunakan endpoint berbeda jika status selesai
      const endpoint =
        tx && tx.status === "siap_ambil"
          ? `/transactions/${id}/whatsapp-completion`
          : `/transactions/${id}/whatsapp-preview`;
      const res = await api.get(endpoint);
      let phone = (res.data.phone || "").replace(/[^0-9]/g, "");
      if (phone.startsWith("0")) phone = "62" + phone.slice(1);
      if (phone.startsWith("62")) phone = phone;
      else if (phone.startsWith("8")) phone = "62" + phone;
      const message = encodeURIComponent(res.data.message || "");
      const waUrl = `https://wa.me/${phone}?text=${message}`;
      window.open(waUrl, "_blank");
    } catch (error) {
      toast.error("Gagal membuka WhatsApp Web");
    }
  };

  const [printSettings, setPrintSettings] = useState({});
  const [showPrinterModal, setShowPrinterModal] = useState(false);
  const [printerConnecting, setPrinterConnecting] = useState(false);

  useEffect(() => {
    api
      .get("/settings")
      .then((res) => setPrintSettings(res.data || {}))
      .catch(() => {});
  }, []);

  const doPrint = async () => {
    try {
      const res = await bluetoothPrinter.printReceipt(tx, printSettings);
      if (res.success) {
        toast.success("Struk berhasil dicetak");
      } else {
        toast.error(`Cetak gagal: ${res.error}`);
      }
    } catch (e) {
      toast.error("Terjadi kesalahan saat mencetak");
    }
  };

  const printReceipt = async () => {
    if (!bluetoothPrinter.isConnected()) {
      if (!bluetoothPrinter.isSupported()) {
        toast.error(
          "Web Bluetooth tidak didukung di browser ini. Gunakan Chrome/Edge.",
        );
        return;
      }
      setShowPrinterModal(true);
      return;
    }
    await doPrint();
  };

  const handleConnectAndPrint = async () => {
    setPrinterConnecting(true);
    const res = await bluetoothPrinter.connect();
    setPrinterConnecting(false);
    setShowPrinterModal(false);
    if (!res.success) {
      toast.error(`Gagal terhubung ke printer: ${res.error}`);
      return;
    }
    await doPrint();
  };

  const fmt = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
  const fmtDt = (d) => {
    if (!d) return "-";
    const dt = new Date(d);
    const date = dt.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const h = String(dt.getHours()).padStart(2, "0");
    const m = String(dt.getMinutes()).padStart(2, "0");
    return `${date}, ${h}:${m}`;
  };

  if (loading)
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary"></div>
      </div>
    );
  if (!tx)
    return <div className="alert alert-danger">Transaksi tidak ditemukan</div>;

  const services = tx.details?.filter((d) => d.item_type === "service") || [];
  const products = tx.details?.filter((d) => d.item_type === "product") || [];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Link to="/transactions" className="text-decoration-none">
            &larr; Kembali
          </Link>
          <h4 className="mt-2 mb-0">{tx.invoice_number}</h4>
        </div>
        <div className="d-flex gap-2">
          {/* WA Selesai: light, only enabled if status is 'siap_ambil', placed left */}
          <button
            className="btn btn-light"
            disabled={tx.status !== "siap_ambil" || tx.status === "cancelled"}
            onClick={async () => {
              try {
                const res = await api.get(
                  `/transactions/${id}/whatsapp-completion`,
                );
                let phone = (res.data.phone || "").replace(/[^0-9]/g, "");
                if (phone.startsWith("0")) phone = "62" + phone.slice(1);
                else if (phone.startsWith("8")) phone = "62" + phone;
                else if (phone.startsWith("62")) phone = phone;
                const message = encodeURIComponent(res.data.message || "");
                const waUrl = `https://wa.me/${phone}?text=${message}`;
                window.open(waUrl, "_blank");
              } catch (error) {
                toast.error("Gagal membuka WhatsApp Web");
              }
            }}
          >
            <FiSend className="me-1" /> WA Selesai
          </button>
          {/* WA Invoice: solid green, disabled for cancelled */}
          <button
            className="btn btn-success"
            disabled={tx.status === "cancelled"}
            onClick={async () => {
              try {
                const res = await api.get(
                  `/transactions/${id}/whatsapp-preview`,
                );
                let phone = (res.data.phone || "").replace(/[^0-9]/g, "");
                if (phone.startsWith("0")) phone = "62" + phone.slice(1);
                else if (phone.startsWith("8")) phone = "62" + phone;
                else if (phone.startsWith("62")) phone = phone;
                const message = encodeURIComponent(res.data.message || "");
                const waUrl = `https://wa.me/${phone}?text=${message}`;
                window.open(waUrl, "_blank");
              } catch (error) {
                toast.error("Gagal membuka WhatsApp Web");
              }
            }}
          >
            <FiSend className="me-1" /> WA Invoice
          </button>
          <button
            className="btn btn-primary"
            onClick={printReceipt}
            disabled={tx.status === "cancelled"}
          >
            <FiPrinter className="me-1" /> Cetak Struk
          </button>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-md-8">
          <div className="card p-3 mb-3">
            <h6>Detail Item &amp; Pembayaran</h6>
            {services.length > 0 && (
              <>
                <h6 className="text-muted small mt-2">Layanan</h6>
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Nama</th>
                      <th className="text-end">Harga</th>
                      <th className="text-end">Qty</th>
                      <th className="text-end">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.map((d) => (
                      <tr key={d.id}>
                        <td>{d.item_name}</td>
                        <td className="text-end">{fmt(d.unit_price)}</td>
                        <td className="text-end">{d.quantity}</td>
                        <td className="text-end fw-bold">{fmt(d.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            {products.length > 0 && (
              <>
                <h6 className="text-muted small mt-2">Produk</h6>
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Nama</th>
                      <th className="text-end">Harga</th>
                      <th className="text-end">Qty</th>
                      <th className="text-end">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((d) => (
                      <tr key={d.id}>
                        <td>{d.item_name}</td>
                        <td className="text-end">{fmt(d.unit_price)}</td>
                        <td className="text-end">{d.quantity}</td>
                        <td className="text-end fw-bold">{fmt(d.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            <hr className="my-2" />
            <table className="table table-sm table-borderless mb-0">
              <tbody>
                <tr>
                  <td className="text-muted">Subtotal</td>
                  <td className="text-end">{fmt(tx.subtotal)}</td>
                </tr>
                {Number(tx.discount_amount) > 0 && (
                  <tr>
                    <td className="text-muted">Diskon</td>
                    <td className="text-end text-danger">
                      -{fmt(tx.discount_amount)}
                    </td>
                  </tr>
                )}
                <tr className="fw-bold">
                  <td>Total</td>
                  <td className="text-end text-primary">
                    {fmt(tx.total_amount)}
                  </td>
                </tr>
                {tx.payment_method === "unpaid" ? (
                  <tr>
                    <td colSpan="2" className="text-center">
                      <Badge bg="danger">BELUM DIBAYAR</Badge>
                    </td>
                  </tr>
                ) : tx.payment_method === "split" ? (
                  <>
                    <tr>
                      <td colSpan="2" className="text-center">
                        <Badge bg="info">SPLIT PAYMENT</Badge>
                      </td>
                    </tr>
                    {(() => {
                      let splits = tx.split_payments || [];
                      if (typeof splits === "string") {
                        try {
                          splits = JSON.parse(splits);
                        } catch {
                          splits = [];
                        }
                      }
                      return splits.map((sp, i) => (
                        <tr key={i}>
                          <td className="text-muted">
                            {(sp.method || "").toUpperCase()}
                          </td>
                          <td className="text-end">{fmt(sp.amount)}</td>
                        </tr>
                      ));
                    })()}
                    {Number(tx.change_amount) > 0 && (
                      <tr>
                        <td className="text-muted">Kembali</td>
                        <td className="text-end">{fmt(tx.change_amount)}</td>
                      </tr>
                    )}
                  </>
                ) : tx.payment_method === "partial" ? (
                  <>
                    <tr>
                      <td colSpan="2" className="text-center">
                        <Badge bg="warning" text="dark">
                          DP / BAYAR SEBAGIAN
                        </Badge>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">
                        DP
                        {tx.dp_payment_method
                          ? ` (${tx.dp_payment_method.toUpperCase()})`
                          : ""}
                      </td>
                      <td className="text-end text-warning fw-bold">
                        {fmt(tx.dp_amount)}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">Sisa Pelunasan</td>
                      <td className="text-end text-danger fw-bold">
                        {fmt(Number(tx.total_amount) - Number(tx.dp_amount))}
                      </td>
                    </tr>
                  </>
                ) : (
                  <>
                    {Number(tx.dp_amount) > 0 && (
                      <tr>
                        <td className="text-muted">
                          DP
                          {tx.dp_payment_method
                            ? ` (${tx.dp_payment_method.toUpperCase()})`
                            : ""}
                        </td>
                        <td className="text-end">{fmt(tx.dp_amount)}</td>
                      </tr>
                    )}
                    <tr>
                      <td className="text-muted">
                        {Number(tx.dp_amount) > 0 ? "Pelunasan" : "Bayar"} (
                        {tx.payment_method?.toUpperCase()})
                      </td>
                      <td className="text-end">{fmt(tx.payment_amount)}</td>
                    </tr>
                    {tx.payment_method === "cash" && (
                      <tr>
                        <td className="text-muted">Kembali</td>
                        <td className="text-end">{fmt(tx.change_amount)}</td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card p-3 mb-3">
            <h6>Info Transaksi</h6>
            <table className="table table-sm table-borderless">
              <tbody>
                <tr>
                  <td className="text-muted">Status</td>
                  <td>
                    <Badge
                      bg={
                        {
                          diterima: "warning",
                          dicuci: "info",
                          disetrika: "primary",
                          selesai_setrika: "dark",
                          siap_ambil: "success",
                          diambil: "secondary",
                          cancelled: "danger",
                        }[tx.status] || "secondary"
                      }
                    >
                      {{
                        diterima: "Diterima",
                        dicuci: "Dicuci",
                        disetrika: "Disetrika",
                        selesai_setrika: "Selesai Setrika",
                        siap_ambil: "Siap Ambil",
                        diambil: "Diambil",
                        cancelled: "Dibatalkan",
                      }[tx.status] || tx.status}
                    </Badge>
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">Customer</td>
                  <td>{tx.customer_name || "-"}</td>
                </tr>

                {tx.weight_kg && (
                  <tr>
                    <td className="text-muted">Berat</td>
                    <td>{tx.weight_kg} kg</td>
                  </tr>
                )}
                {tx.total_pcs && (
                  <tr>
                    <td className="text-muted">Jumlah</td>
                    <td>{tx.total_pcs} pcs</td>
                  </tr>
                )}
                {((tx.machines && tx.machines.length > 0) ||
                  canEditMachines) && (
                  <tr>
                    <td className="text-muted">Mesin Cuci</td>
                    <td>
                      <div className="d-flex flex-wrap align-items-center gap-1">
                        {(tx.machines || []).map((m) => (
                          <span
                            key={m.id}
                            className="badge bg-info d-inline-flex align-items-center gap-1"
                          >
                            <MdLocalLaundryService size={13} />
                            {m.name}
                          </span>
                        ))}
                        {(!tx.machines || tx.machines.length === 0) && (
                          <span className="text-muted">-</span>
                        )}
                        {canEditMachines && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 ms-1 text-decoration-none"
                            style={{ fontSize: "0.78rem" }}
                            onClick={openMachineModal}
                          >
                            <FiEdit2 size={12} className="me-1" />
                            Ubah
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                {tx.packing_count && (
                  <tr>
                    <td className="text-muted">Packingan</td>
                    <td>
                      <span className="badge bg-info">
                        {tx.packing_count} pack
                      </span>
                    </td>
                  </tr>
                )}
                {tx.pelipat_name && (
                  <tr>
                    <td className="text-muted">Pelipat</td>
                    <td>{tx.pelipat_name}</td>
                  </tr>
                )}
                {tx.estimated_done && (
                  <tr>
                    <td className="text-muted">Estimasi Selesai</td>
                    <td>{fmtDt(tx.estimated_done)}</td>
                  </tr>
                )}
                <tr>
                  <td className="text-muted">Kasir</td>
                  <td>{tx.cashier_name}</td>
                </tr>
                <tr>
                  <td className="text-muted">Tgl Diterima</td>
                  <td>{fmtDt(tx.created_at)}</td>
                </tr>
                {tx.paid_at && (
                  <tr>
                    <td className="text-muted">Tgl Dibayar</td>
                    <td className="text-success fw-semibold">
                      {fmtDt(tx.paid_at)}
                    </td>
                  </tr>
                )}
                {tx.status === "cancelled" && tx.cancelled_at && (
                  <tr>
                    <td className="text-muted">Tgl Dibatalkan</td>
                    <td className="text-danger fw-semibold">
                      {fmtDt(tx.cancelled_at)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {tx.clothing_notes && (
              <div
                className="rounded px-3 py-2 mt-1"
                style={{ background: "#fff8e1", border: "1px solid #ffe082" }}
              >
                <div
                  className="small fw-semibold mb-1"
                  style={{ color: "#b45309" }}
                >
                  📝 Catatan Pakaian
                </div>
                <div
                  style={{
                    fontSize: "0.88rem",
                    color: "#78350f",
                    lineHeight: 1.5,
                  }}
                >
                  {tx.clothing_notes}
                </div>
              </div>
            )}
            {tx.status === "cancelled" && tx.cancel_reason && (
              <div
                className="rounded px-3 py-2 mt-2"
                style={{ background: "#fef2f2", border: "1px solid #fca5a5" }}
              >
                <div
                  className="small fw-semibold mb-1"
                  style={{ color: "#dc2626" }}
                >
                  ❌ Alasan Pembatalan
                </div>
                <div
                  style={{
                    fontSize: "0.88rem",
                    color: "#7f1d1d",
                    lineHeight: 1.5,
                  }}
                >
                  {tx.cancel_reason}
                </div>
              </div>
            )}
          </div>

          {/* Loyalty Progress */}
          {(tx.loyalty_progress || []).length > 0 && (
            <div className="card p-3 mb-3">
              <h6>Progress Loyalti</h6>
              {tx.loyalty_progress.map((lp) => {
                const pct = Math.min(
                  100,
                  (lp.current_count / lp.required_count) * 100,
                );
                const canRedeem = lp.current_count >= lp.required_count;
                return (
                  <div key={lp.rule_id} className="mb-2">
                    <div className="d-flex justify-content-between small">
                      <span>{lp.rule_name}</span>
                      <span className="fw-bold">
                        {lp.current_count}/{lp.required_count}
                      </span>
                    </div>
                    <div className="progress" style={{ height: "8px" }}>
                      <div
                        className={`progress-bar ${canRedeem ? "bg-success" : "bg-primary"}`}
                        style={{ width: `${pct}%` }}
                      ></div>
                    </div>
                    {canRedeem && (
                      <div className="text-end">
                        <Badge bg="success" style={{ fontSize: "0.65rem" }}>
                          BISA REDEEM — {lp.reward_service_name}
                        </Badge>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {((tx.logs || []).length > 0 || tx.created_at) && (
            <div className="card p-3 mb-3">
              <h6 className="mb-3">Riwayat Proses</h6>
              <div style={{ position: "relative" }}>
                {/* vertical line */}
                <div
                  style={{
                    position: "absolute",
                    left: 15,
                    top: 0,
                    bottom: 0,
                    width: 2,
                    background: "rgba(128,128,128,0.2)",
                  }}
                />

                {/* Diterima — always first, from created_at */}
                {(() => {
                  const dt = new Date(tx.created_at);
                  const timeStr =
                    String(dt.getHours()).padStart(2, "0") +
                    ":" +
                    String(dt.getMinutes()).padStart(2, "0");
                  const dateStr = dt.toLocaleDateString("id-ID", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  });
                  return (
                    <div
                      className="d-flex gap-3 mb-3"
                      style={{ position: "relative" }}
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          background: "#ffc10722",
                          border: "2px solid #ffc107",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          zIndex: 1,
                        }}
                      >
                        <Tag2 size={16} color="#ffc107" variant="Bold" />
                      </div>
                      <div style={{ flex: 1, paddingTop: 4 }}>
                        <div
                          className="fw-semibold"
                          style={{ fontSize: "0.85rem", color: "#ffc107" }}
                        >
                          Diterima
                        </div>
                        <div
                          style={{ fontSize: "0.78rem" }}
                          className="text-muted"
                        >
                          {tx.cashier_name}
                        </div>
                        <div
                          style={{ fontSize: "0.75rem" }}
                          className="text-muted mt-1"
                        >
                          {dateStr} · {timeStr}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {(tx.logs || []).map((log, i) => {
                  const statusLabel = {
                    diterima: "Diterima",
                    dicuci: "Dicuci",
                    disetrika: "Disetrika",
                    selesai_setrika: "Selesai Setrika",
                    siap_ambil: "Siap Ambil",
                    diambil: "Diambil",
                    cancelled: "Dibatalkan",
                  };
                  const dotColor = {
                    diterima: "#ffc107",
                    dicuci: "#0dcaf0",
                    disetrika: "#0d6efd",
                    selesai_setrika: "#212529",
                    siap_ambil: "#198754",
                    diambil: "#6c757d",
                    cancelled: "#dc3545",
                  };
                  const dotIcon = {
                    diterima: (
                      <Tag2
                        size={16}
                        color={dotColor.diterima}
                        variant="Bold"
                      />
                    ),
                    dicuci: (
                      <Refresh2
                        size={16}
                        color={dotColor.dicuci}
                        variant="Bold"
                      />
                    ),
                    disetrika: (
                      <Magicpen
                        size={16}
                        color={dotColor.disetrika}
                        variant="Bold"
                      />
                    ),
                    selesai_setrika: (
                      <TickCircle
                        size={16}
                        color={dotColor.selesai_setrika}
                        variant="Bold"
                      />
                    ),
                    siap_ambil: (
                      <Box
                        size={16}
                        color={dotColor.siap_ambil}
                        variant="Bold"
                      />
                    ),
                    diambil: (
                      <TickCircle
                        size={16}
                        color={dotColor.diambil}
                        variant="Bold"
                      />
                    ),
                    cancelled: (
                      <CloseCircle
                        size={16}
                        color={dotColor.cancelled}
                        variant="Bold"
                      />
                    ),
                  };
                  const color = dotColor[log.to_status] || "#6c757d";
                  const label = statusLabel[log.to_status] || log.to_status;
                  const dt = new Date(log.changed_at);
                  const timeStr =
                    String(dt.getHours()).padStart(2, "0") +
                    ":" +
                    String(dt.getMinutes()).padStart(2, "0");
                  const dateStr = dt.toLocaleDateString("id-ID", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  });
                  return (
                    <div
                      key={log.id || i}
                      className="d-flex gap-3 mb-3"
                      style={{ position: "relative" }}
                    >
                      {/* dot */}
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          background: color + "22",
                          border: `2px solid ${color}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          zIndex: 1,
                        }}
                      >
                        {dotIcon[log.to_status] || (
                          <TickCircle size={16} color={color} variant="Bold" />
                        )}
                      </div>
                      {/* content */}
                      <div style={{ flex: 1, paddingTop: 4 }}>
                        <div
                          className="fw-semibold"
                          style={{ fontSize: "0.85rem", color }}
                        >
                          {label}
                        </div>
                        <div
                          style={{ fontSize: "0.78rem" }}
                          className="text-muted"
                        >
                          {log.changed_by_name}
                        </div>
                        <div
                          style={{ fontSize: "0.75rem" }}
                          className="text-muted mt-1"
                        >
                          {dateStr} · {timeStr}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Ubah Mesin Cuci Modal */}
      <Modal
        show={showMachineModal}
        onHide={() => setShowMachineModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>
            Ubah Mesin Cuci
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3 text-muted small">
            Transaksi <strong>{tx?.invoice_number}</strong> — pilih mesin yang
            dipakai. Bisa lebih dari satu.
          </p>
          {washingMachines.length === 0 ? (
            <div className="alert alert-warning mb-0 py-2 small">
              Belum ada mesin cuci aktif. Tambahkan dulu di menu{" "}
              <strong>Master Data &rsaquo; Mesin Cuci</strong>.
            </div>
          ) : (
            <div className="machine-picker">
              {washingMachines.map((m) => {
                const checked = selectedMachineIds.includes(m.id);
                return (
                  <label
                    key={m.id}
                    className={`machine-option ${checked ? "selected" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleMachine(m.id)}
                    />
                    <MdLocalLaundryService className="machine-option-icon" />
                    <span className="machine-option-label">{m.name}</span>
                    {m.capacity_kg != null && (
                      <span className="machine-option-meta">
                        {Number(m.capacity_kg)} kg
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          )}
          {selectedMachineIds.length > 0 && (
            <div className="mt-3 small text-muted">
              {selectedMachineIds.length} mesin dipilih
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowMachineModal(false)}
            disabled={machineSaving}
          >
            Batal
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={saveMachines}
            disabled={machineSaving || selectedMachineIds.length === 0}
          >
            {machineSaving ? "Menyimpan..." : "Simpan"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Printer Connect Modal */}
      <Modal
        show={showPrinterModal}
        onHide={() => setShowPrinterModal(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Printer Belum Terhubung</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Hubungkan printer Bluetooth sekarang untuk mencetak struk?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowPrinterModal(false)}
            disabled={printerConnecting}
          >
            Batal
          </Button>
          <Button
            variant="primary"
            onClick={handleConnectAndPrint}
            disabled={printerConnecting}
          >
            {printerConnecting ? "Menghubungkan..." : "Hubungkan & Cetak"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TransactionDetail;
